import { ChangeDetectorRef, Component, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { SnackbarService } from './_services/snackbar.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit {
  private readonly forbiddenPatterns = [
    // Cyrillic characters
    /[\u0400-\u04FF\u0500-\u052F\u2DE0-\u2DFF\uA640-\uA69F]/g,

    // Zero-width characters
    /[\u200B-\u200D\u2060\uFEFF]/g,

    // Function application characters
    /[\u2061-\u2064]/g,

    // Spaces and hyphens
    /[\u00AD\u00A0\u1680\u2000-\u200A\u202F\u205F\u3000]/g,

    // Ligatures
    /[\uFB00-\uFB06\uFB13-\uFB17]/g,

    // Punctuation and symbols
    /[\u2010-\u2027\u2030-\u205E]/g,

    // Control characters
    /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F\u0080-\u009F]/g,

    // Bidirectional controls
    /[\u200E\u200F\u202A-\u202E\u2066-\u2069\u061C]/g,

    // Combining marks
    /[\u0300-\u036F\u1AB0-\u1AFF\u1DC0-\u1DFF\u20D0-\u20FF\uFE20-\uFE2F]/g,

    // Format characters
    /[\u2028\u2029]/g,

    // Variation selectors
    /[\uFE00-\uFE0F]/g,

    // Special characters
    /[\uFFFC\uFFFD]/g,

    // Newline characters
    /\r\n|\r|\n/g,
  ];

  hasInvalidChars(str: string): boolean {
    return this.forbiddenPatterns.some((pattern) => pattern.test(str));
  }

  sanitize(str: string): string {
    // First normalize to canonical decomposition
    let cleaned = str.normalize('NFKD');
    cleaned = cleaned.replace(/\r\n|\r|\n/g, ' ');

    // Apply each pattern separately
    this.forbiddenPatterns.forEach((pattern) => {
      cleaned = cleaned.replace(pattern, '');
    });

    // Final normalization to composed form
    cleaned = cleaned.normalize('NFC');

    return cleaned;
  }
  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private changes: ChangeDetectorRef,
    private snackbar: SnackbarService,
  ) {}

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      document.addEventListener('paste', (e: ClipboardEvent) => {
        const el = e.target as HTMLElement;
        if (
          el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          (el as HTMLElement).isContentEditable
        ) {
          e.preventDefault();

          const pasted = e.clipboardData?.getData('text') || '';
          const hasForbidden = this.hasInvalidChars(pasted);
          const sanitized = this.sanitize(pasted);

          if ('value' in el) {
            const input = el as HTMLInputElement | HTMLTextAreaElement;
            const start = input.selectionStart ?? input.value.length;
            const end = input.selectionEnd ?? input.value.length;
            input.value = input.value.substring(0, start) + sanitized + input.value.substring(end);

            if (this.canSetSelectionRange(input)) {
              input.setSelectionRange(start + sanitized.length, start + sanitized.length);
            }
          } else if ((el as HTMLElement).isContentEditable) {
            (document as any).execCommand('insertText', false, sanitized);
          }

          if (hasForbidden && this.snackbar) {
            this.snackbar.openSnackBar('Invalid characters has been removed.');
          }

          el.dispatchEvent(new Event('input', { bubbles: true }));
          this.changes.detectChanges();
        }
      });
    }
  }

  canSetSelectionRange(el: HTMLInputElement | HTMLTextAreaElement): boolean {
    return (
      el instanceof HTMLTextAreaElement ||
      ['text', 'search', 'tel', 'url', 'password'].includes(el.type)
    );
  }
}
